import type { UnifiedTableRecord } from "../../../shared/ui/data-table/model/data-table-contract";

export interface FlowsheetDetailRow {
  label: string;
  values: Record<string, string>;
}

export interface FlowsheetDetail {
  id: string;
  flowsheetId: string;
  name: string;
  columns: string[];
  rows: FlowsheetDetailRow[];
}

const createVisitRow = (
  columns: readonly string[],
  label: string,
  values: string[],
): FlowsheetDetailRow => ({
  label,
  values: columns.reduce<Record<string, string>>((acc, column, index) => {
    acc[column] = values[index] ?? "—";
    return acc;
  }, {}),
});

const createFlowsheetDetail = (
  id: string,
  flowsheetId: string,
  name: string,
  columns: readonly string[],
  rows: Array<[string, string[]]>,
): FlowsheetDetail => ({
  id,
  flowsheetId,
  name,
  columns: [...columns],
  rows: rows.map(([label, values]) => createVisitRow(columns, label, values)),
});

const lumbarVisitColumns = [
  "Visit 1",
  "Visit 2",
  "Visit 3",
  "Visit 4",
  "Visit 5",
  "Visit 6",
  "Visit 7",
  "Visit 8",
  "Visit 9",
  "Visit 10",
  "Visit 11/G-Code",
  "Visit 12",
  "Visit 13",
] as const;

const createLumbarVisitRow = (
  label: string,
  values: string[],
): FlowsheetDetailRow => createVisitRow(lumbarVisitColumns, label, values);

const patientIntakeColumns = [
  "Intake",
  "Nursing Review",
  "Provider Review",
  "Discharge",
] as const;

const pediatricVitalColumns = [
  "Visit 1",
  "Visit 2",
  "Visit 3",
  "Visit 4",
  "Visit 5",
  "Visit 6",
  "Visit 7",
  "Visit 8",
] as const;

const physicalTherapyColumns = [
  "Eval",
  "Visit 2",
  "Visit 3",
  "Visit 4",
  "Visit 5",
  "Visit 6",
  "Visit 7",
  "Visit 8",
  "Visit 9",
  "Visit 10",
] as const;

const nutritionScreeningColumns = [
  "Screen 1",
  "Screen 2",
  "Screen 3",
  "Screen 4",
  "Follow Up",
] as const;

const intakeOutputColumns = [
  "Day 1 AM",
  "Day 1 PM",
  "Day 2 AM",
  "Day 2 PM",
  "Day 3 AM",
  "Day 3 PM",
] as const;

const woundCareColumns = [
  "Assessment 1",
  "Treatment 2",
  "Treatment 3",
  "Treatment 4",
  "Treatment 5",
  "Treatment 6",
  "Treatment 7",
  "Discharge",
] as const;

export const flowsheetDetails: FlowsheetDetail[] = [
  {
    id: "lumbar-rehab-flowsheet",
    flowsheetId: "165894",
    name: "D/C*****REHAB: Lumbar Flowsheet",
    columns: [...lumbarVisitColumns],
    rows: [
      createLumbarVisitRow("Date of Treatment", [
        "02/24/2017",
        "03/01/2017",
        "03/03/2017",
        "03/08/2017",
        "03/10/2017",
        "03/15/2017",
        "03/17/2017",
        "03/22/2017",
        "03/24/2017",
        "03/28/2017",
        "03/30/2017",
        "04/04/2017",
        "04/06/2017",
      ]),
      createLumbarVisitRow("Onset Date", ["01/01/2017"]),
      createLumbarVisitRow("PT Eval", ["25'"]),
      createLumbarVisitRow("Biostep / Airdyne / UBE / Bicycle", [
        "—",
        "5",
        "5'",
        "5'",
        "5'",
        "5'",
        "5'",
        "5'",
        "5'",
        "5'",
        "5",
        "5'",
        "5'",
      ]),
      createLumbarVisitRow("Standing / Seated / Supine Hamstring Stretch", [
        "—",
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30" supine',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
      ]),
      createLumbarVisitRow("Standing Heel Cord Stretch", [
        "—",
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
      ]),
      createLumbarVisitRow("Knee to Chest", [
        "—",
        "—",
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
      ]),
      createLumbarVisitRow("Low Trunk Rotation", [
        "—",
        "> unable",
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
      ]),
      createLumbarVisitRow("Piriformis Stretch", [
        "—",
        "> unable",
        ">",
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
        '3×30"',
      ]),
      createLumbarVisitRow("Prone on Elbows / Press Ups", [
        '10×10"',
        ">",
        '10×10"',
        "help",
      ]),
      createLumbarVisitRow("Pelvic Tilts / Crunches (+PB)", [
        "—",
        ">",
        '1/15/5"',
        '2/10/5"',
      ]),
      createLumbarVisitRow("Bridges (+PB)", ["—", ">", '1/10/5"', '2/10/5"']),
      createLumbarVisitRow("Single LE Hip Ex", ["prone 2×10"]),
      createLumbarVisitRow("Home Exercise Program", ["HEP"]),
      createLumbarVisitRow(
        "Education: 1-Safety, 2-Activity level, 3-Disease process, 4-Self-care, 5-Equipment",
        [
          "—",
          "1-5",
          "1-5",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
          "2-4",
        ],
      ),
      createLumbarVisitRow(
        "Methods: Written Handouts / Verbal / Individual Instruct",
        [
          "—",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
          "vi",
        ],
      ),
      createLumbarVisitRow("Instructed: Patient / Family / Caregiver", [
        "—",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
        "p",
      ]),
      createLumbarVisitRow(
        "Understanding: Verbalizes Understanding / Return Demonstration / More Instruct",
        [
          "—",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
          "vrm",
        ],
      ),
      createLumbarVisitRow("Therex Minutes", [
        "10'",
        "17",
        "35",
        "34",
        "20",
        "19",
        "20",
        "20",
        "20",
        "19",
        "17",
        "19",
        "20",
      ]),
      createLumbarVisitRow("Traction Minutes / Pounds", [
        "—",
        "—",
        "—",
        "—",
        "15@ 65-80#",
        "15 @ 75-90#",
        "15 80-100#",
        "15 90-110#",
        "15 90-110#",
        "15 100-120#",
        "15 100-120#",
        "15 100-120#",
        "15 100-120#",
      ]),
      createLumbarVisitRow("Manual", ["—", "15"]),
      createLumbarVisitRow("Ultrasound Pulsed w/cm2", [
        "—",
        "10 cont. 1.2 w/cm2",
      ]),
      createLumbarVisitRow("Electric Stim IFC 80-150 w/MH/CP", [
        "IFC/MHP 15'",
        "15 w/mh",
        "15 w/mh",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
        "15 w/cp",
      ]),
      createLumbarVisitRow("Time Based Minutes", [
        "10",
        "27",
        "35",
        "34",
        "35",
        "34",
        "35",
        "35",
        "35",
        "34",
        "32",
        "34",
        "35",
      ]),
      createLumbarVisitRow("Total Minutes", [
        "50'",
        "57",
        "50",
        "49",
        "50",
        "49",
        "50",
        "50",
        "50",
        "49",
        "47",
        "49",
        "50",
      ]),
      createLumbarVisitRow("Initials of Provider", [
        "AG",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
        "CC",
      ]),
    ],
  },
  createFlowsheetDetail(
    "comprehensive-patient-intake",
    "165893",
    "COMPREHENSIVE PATIENT INTAKE",
    patientIntakeColumns,
    [
      [
        "Date of Service",
        ["12/02/2015", "12/02/2015", "12/02/2015", "12/02/2015"],
      ],
      [
        "Chief Complaint",
        ["Follow-up visit", "Reviewed", "Reviewed", "Resolved"],
      ],
      ["History Provided By", ["Patient", "Patient", "Patient", "Patient"]],
      ["Preferred Language", ["English", "English", "English", "English"]],
      ["Allergies Reviewed", ["Yes", "Yes", "Yes", "Yes"]],
      [
        "Medication Review",
        ["Completed", "No changes", "Updated", "Completed"],
      ],
      [
        "Past Medical History",
        ["Reviewed", "Reviewed", "Reviewed", "Reviewed"],
      ],
      ["Surgical History", ["Reviewed", "Reviewed", "—", "—"]],
      ["Family History", ["Reviewed", "Reviewed", "—", "—"]],
      ["Social History", ["Reviewed", "Reviewed", "—", "—"]],
      ["Tobacco Use", ["Never", "Never", "Never", "Never"]],
      ["Alcohol Use", ["Occasional", "Occasional", "Occasional", "Occasional"]],
      ["Fall Risk", ["Low", "Low", "Low", "Low"]],
      ["Pain Score", ["2/10", "2/10", "1/10", "0/10"]],
      ["Education Provided", ["Yes", "Yes", "Yes", "Yes"]],
      [
        "Patient Understanding",
        ["Verbalized", "Verbalized", "Verbalized", "Verbalized"],
      ],
      ["Disposition", ["Roomed", "Provider", "Checkout", "Discharged"]],
      ["Initials of Provider", ["MR", "KT", "DR", "KT"]],
    ],
  ),
  createFlowsheetDetail(
    "pediatric-vital-signs",
    "165892",
    "PEDIATRIC VITAL SIGNS",
    pediatricVitalColumns,
    [
      [
        "Date of Treatment",
        [
          "11/20/2015",
          "12/04/2015",
          "01/08/2016",
          "02/12/2016",
          "03/18/2016",
          "04/15/2016",
          "05/13/2016",
          "06/10/2016",
        ],
      ],
      [
        "Temperature",
        ["98.6", "98.4", "99.1", "98.7", "98.3", "98.8", "98.5", "98.6"],
      ],
      [
        "Temperature Source",
        ["Oral", "Oral", "Oral", "Oral", "Oral", "Oral", "Oral", "Oral"],
      ],
      ["Pulse", ["82", "84", "88", "80", "78", "86", "82", "80"]],
      ["Respirations", ["18", "18", "20", "18", "18", "20", "18", "18"]],
      [
        "Blood Pressure",
        [
          "102/68",
          "104/66",
          "100/64",
          "106/70",
          "104/68",
          "108/70",
          "106/68",
          "104/66",
        ],
      ],
      [
        "Oxygen Saturation",
        ["99%", "99%", "98%", "99%", "100%", "99%", "99%", "100%"],
      ],
      [
        "Height",
        [
          "46 in",
          "46.5 in",
          "47 in",
          "47.5 in",
          "48 in",
          "48.5 in",
          "49 in",
          "49.25 in",
        ],
      ],
      [
        "Weight",
        [
          "48 lb",
          "49 lb",
          "49.5 lb",
          "50 lb",
          "51 lb",
          "52 lb",
          "52.5 lb",
          "53 lb",
        ],
      ],
      ["BMI", ["15.9", "15.9", "15.8", "15.7", "15.8", "15.9", "15.8", "15.9"]],
      ["Pain Score", ["0", "0", "2", "0", "0", "1", "0", "0"]],
      [
        "Growth Chart Reviewed",
        ["Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes", "Yes"],
      ],
      [
        "Immunization Status",
        [
          "Current",
          "Current",
          "Current",
          "Current",
          "Current",
          "Current",
          "Current",
          "Current",
        ],
      ],
      [
        "Initials of Provider",
        ["SP", "SP", "AR", "SP", "AR", "SP", "AR", "SP"],
      ],
    ],
  ),
  createFlowsheetDetail(
    "comprehensive-physical-therapy",
    "165891",
    "COMPREHENSIVE PHYSICAL THERAPY FLOWSHEET",
    physicalTherapyColumns,
    [
      [
        "Date of Treatment",
        [
          "10/06/2015",
          "10/08/2015",
          "10/13/2015",
          "10/15/2015",
          "10/20/2015",
          "10/22/2015",
          "10/27/2015",
          "10/29/2015",
          "11/03/2015",
          "11/05/2015",
        ],
      ],
      [
        "Pain Current",
        [
          "5/10",
          "5/10",
          "4/10",
          "4/10",
          "3/10",
          "3/10",
          "2/10",
          "2/10",
          "1/10",
          "1/10",
        ],
      ],
      [
        "Pain Worst",
        [
          "8/10",
          "7/10",
          "7/10",
          "6/10",
          "6/10",
          "5/10",
          "4/10",
          "4/10",
          "3/10",
          "3/10",
        ],
      ],
      [
        "Gait",
        [
          "Antalgic",
          "Antalgic",
          "Improved",
          "Improved",
          "Mild limp",
          "Mild limp",
          "Normalizing",
          "Normalizing",
          "Normal",
          "Normal",
        ],
      ],
      [
        "Assistive Device",
        [
          "Cane",
          "Cane",
          "Cane",
          "None",
          "None",
          "None",
          "None",
          "None",
          "None",
          "None",
        ],
      ],
      [
        "Ankle AROM Dorsiflexion",
        ["0", "2", "4", "5", "6", "8", "9", "10", "10", "12"],
      ],
      [
        "Ankle AROM Plantarflexion",
        ["35", "36", "38", "40", "42", "43", "45", "45", "46", "48"],
      ],
      [
        "Quad Sets",
        [
          "2×10",
          "2×10",
          "3×10",
          "3×10",
          "3×10",
          "3×10",
          "3×10",
          "3×10",
          "HEP",
          "HEP",
        ],
      ],
      [
        "Straight Leg Raise",
        [
          "1×10",
          "2×10",
          "2×10",
          "2×12",
          "3×10",
          "3×10",
          "3×12",
          "3×12",
          "3×15",
          "3×15",
        ],
      ],
      [
        "Heel Slides",
        [
          "2×10",
          "2×10",
          "2×12",
          "3×10",
          "3×10",
          "3×12",
          "3×12",
          "3×15",
          "HEP",
          "HEP",
        ],
      ],
      [
        "Bike",
        [
          "—",
          "5 min",
          "6 min",
          "6 min",
          "8 min",
          "8 min",
          "10 min",
          "10 min",
          "10 min",
          "10 min",
        ],
      ],
      [
        "Balance Training",
        [
          "—",
          "—",
          "Tandem",
          "Tandem",
          "SLS 10 sec",
          "SLS 15 sec",
          "SLS 20 sec",
          "Foam",
          "Foam",
          "Foam",
        ],
      ],
      [
        "Manual Therapy",
        [
          "10 min",
          "10 min",
          "8 min",
          "8 min",
          "5 min",
          "5 min",
          "—",
          "—",
          "—",
          "—",
        ],
      ],
      [
        "Treatment Minutes",
        ["35", "40", "42", "42", "45", "45", "45", "45", "40", "40"],
      ],
      [
        "Initials of Provider",
        ["JM", "JM", "KL", "JM", "KL", "JM", "KL", "JM", "KL", "JM"],
      ],
    ],
  ),
  createFlowsheetDetail(
    "nutrition-screening",
    "165890",
    "NUTRITION SCREENING FLOWSHEET",
    nutritionScreeningColumns,
    [
      [
        "Screen Date",
        ["09/15/2015", "09/22/2015", "09/29/2015", "10/06/2015", "10/13/2015"],
      ],
      ["Height", ["5'4\"", "5'4\"", "5'4\"", "5'4\"", "5'4\""]],
      ["Weight", ["142 lb", "141 lb", "141 lb", "140 lb", "140 lb"]],
      ["BMI", ["24.4", "24.2", "24.2", "24.0", "24.0"]],
      ["Appetite Change", ["No", "No", "No", "Improved", "No"]],
      ["Recent Weight Loss", ["No", "No", "No", "No", "No"]],
      ["Nausea / Vomiting", ["No", "No", "No", "No", "No"]],
      ["Chewing Difficulty", ["No", "No", "No", "No", "No"]],
      ["Swallowing Difficulty", ["No", "No", "No", "No", "No"]],
      ["Diet Ordered", ["Regular", "Regular", "Regular", "Regular", "Regular"]],
      ["Percent Meal Intake", ["75%", "80%", "85%", "90%", "90%"]],
      ["Supplement Ordered", ["—", "—", "Ensure daily", "Ensure daily", "PRN"]],
      [
        "Nutrition Education",
        ["Provided", "Reviewed", "Reviewed", "Reviewed", "Completed"],
      ],
      ["Nutrition Risk", ["Low", "Low", "Low", "Low", "Low"]],
      ["Initials of Provider", ["ND", "ND", "ND", "ND", "ND"]],
    ],
  ),
  createFlowsheetDetail(
    "adult-intake-output",
    "165889",
    "ADULT INTAKE AND OUTPUT FLOWSHEET",
    intakeOutputColumns,
    [
      [
        "Record Date",
        [
          "08/12/2015",
          "08/12/2015",
          "08/13/2015",
          "08/13/2015",
          "08/14/2015",
          "08/14/2015",
        ],
      ],
      [
        "Oral Intake",
        ["620 mL", "480 mL", "540 mL", "600 mL", "720 mL", "520 mL"],
      ],
      ["IV Intake", ["500 mL", "250 mL", "500 mL", "250 mL", "250 mL", "—"]],
      ["Tube Feeding", ["—", "—", "—", "—", "—", "—"]],
      [
        "Total Intake",
        ["1120 mL", "730 mL", "1040 mL", "850 mL", "970 mL", "520 mL"],
      ],
      ["Urine", ["850 mL", "600 mL", "900 mL", "700 mL", "800 mL", "650 mL"]],
      ["Stool", ["—", "1", "—", "1", "—", "—"]],
      ["Emesis", ["—", "—", "—", "—", "—", "—"]],
      ["Drain Output", ["—", "—", "50 mL", "40 mL", "25 mL", "—"]],
      [
        "Total Output",
        ["850 mL", "600 mL", "950 mL", "740 mL", "825 mL", "650 mL"],
      ],
      [
        "Balance",
        ["+270 mL", "+130 mL", "+90 mL", "+110 mL", "+145 mL", "-130 mL"],
      ],
      ["Fluid Restriction", ["No", "No", "No", "No", "No", "No"]],
      ["Nurse Initials", ["LW", "LW", "KP", "KP", "LW", "KP"]],
    ],
  ),
  createFlowsheetDetail(
    "wound-care",
    "165888",
    "WOUND CARE ASSESSMENT FLOWSHEET",
    woundCareColumns,
    [
      [
        "Date of Treatment",
        [
          "07/06/2015",
          "07/08/2015",
          "07/10/2015",
          "07/13/2015",
          "07/15/2015",
          "07/17/2015",
          "07/20/2015",
          "07/22/2015",
        ],
      ],
      [
        "Location",
        [
          "Left ankle",
          "Left ankle",
          "Left ankle",
          "Left ankle",
          "Left ankle",
          "Left ankle",
          "Left ankle",
          "Left ankle",
        ],
      ],
      [
        "Wound Length",
        [
          "2.4 cm",
          "2.3 cm",
          "2.1 cm",
          "1.9 cm",
          "1.7 cm",
          "1.4 cm",
          "1.1 cm",
          "0.8 cm",
        ],
      ],
      [
        "Wound Width",
        [
          "1.2 cm",
          "1.1 cm",
          "1.0 cm",
          "0.9 cm",
          "0.8 cm",
          "0.6 cm",
          "0.5 cm",
          "0.3 cm",
        ],
      ],
      [
        "Wound Depth",
        [
          "0.2 cm",
          "0.2 cm",
          "0.2 cm",
          "0.1 cm",
          "0.1 cm",
          "0.1 cm",
          "0.1 cm",
          "Closed",
        ],
      ],
      [
        "Drainage",
        [
          "Scant",
          "Scant",
          "Scant",
          "Minimal",
          "Minimal",
          "Minimal",
          "None",
          "None",
        ],
      ],
      [
        "Drainage Color",
        ["Serous", "Serous", "Serous", "Serous", "Serous", "Clear", "—", "—"],
      ],
      [
        "Odor",
        ["None", "None", "None", "None", "None", "None", "None", "None"],
      ],
      [
        "Periwound Skin",
        [
          "Intact",
          "Intact",
          "Intact",
          "Pink",
          "Pink",
          "Pink",
          "Intact",
          "Intact",
        ],
      ],
      [
        "Wound Bed",
        [
          "Granulation",
          "Granulation",
          "Granulation",
          "Granulation",
          "Epithelializing",
          "Epithelializing",
          "Epithelializing",
          "Closed",
        ],
      ],
      ["Cleansed With", ["NS", "NS", "NS", "NS", "NS", "NS", "NS", "NS"]],
      [
        "Dressing",
        [
          "Dry gauze",
          "Dry gauze",
          "Foam",
          "Foam",
          "Foam",
          "Foam",
          "Bandage",
          "Open to air",
        ],
      ],
      [
        "Patient Tolerance",
        ["Good", "Good", "Good", "Good", "Good", "Good", "Good", "Good"],
      ],
      [
        "Education Provided",
        [
          "Yes",
          "Reviewed",
          "Reviewed",
          "Reviewed",
          "Reviewed",
          "Reviewed",
          "Reviewed",
          "Complete",
        ],
      ],
      [
        "Initials of Provider",
        ["WC", "WC", "AM", "WC", "AM", "WC", "AM", "WC"],
      ],
    ],
  ),
];

export const flowsheetsRecord: UnifiedTableRecord = {
  module: "flowsheets",
  pageTitle: "Flowsheets",
  sections: [
    {
      id: "flowsheets",
      title: "Flowsheets",
      tables: [
        {
          id: "flowsheets-table",
          columns: [
            {
              key: "flowsheetId",
              header: "Flowsheet ID",
              sortable: true,
              width: 200,
              rendererId: "flowsheetIdWithAction",
            },
            {
              key: "name",
              header: "Flowsheet Name",
              sortable: true,
              width: 420,
            },
            {
              key: "spacer",
              header: "",
              rendererId: "spacer",
            },
          ],
          rows: flowsheetDetails.map(({ id, flowsheetId, name }) => ({
            id,
            flowsheetId,
            name,
            spacer: "",
          })),
          ui: {
            searchable: true,
            searchPlaceholder: "Search...",
            searchKeys: ["flowsheetId", "name"],
            downloadable: ["csv", "xls", "pdf"],
            showTableDownloads: true,
            defaultRowsPerPage: 10,
            rowsPerPageOptions: [5, 10, 20, 25],
            rowHover: true,
            stripedRows: true,
          },
        },
      ],
    },
  ],
};

export const getFlowsheetById = (id?: string): FlowsheetDetail =>
  flowsheetDetails.find((flowsheet) => flowsheet.id === id) ??
  flowsheetDetails[0]!;
